import React, { MouseEvent, ReactNode, useState } from 'react'

import { Popover, SxProps } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Tooltip from 'components_new/atoms/Tooltip'

import SearchAndSelect, {
  SearchAndSelectProps
} from 'components_new/molecules/SearchAndSelect'

export type SearchAndSelectPopoverProps = SearchAndSelectProps & {
  children: ReactNode // trigger
  popoverContent?: ReactNode
  popoverWidth?: number
  position?: 'bottom' | 'top'
  sx?: SxProps
  tooltip?: ReactNode
}

const SearchAndSelectPopover = React.forwardRef<
  HTMLSpanElement,
  SearchAndSelectPopoverProps
>((props, ref) => {
  const {
    children,
    disabled,
    popoverContent,
    popoverWidth,
    position,
    sx,
    tooltip,
    ...searchAndSelectProps
  } = props

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (disabled) return

    event.preventDefault()
    event.stopPropagation()
    if (searchAndSelectProps.onFocus) searchAndSelectProps.onFocus()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    if (searchAndSelectProps.onBlur) searchAndSelectProps.onBlur()
  }

  // Close the popover on selection when only a single value can be selected,
  // and on save when several can.
  const selectProps = searchAndSelectProps as SearchAndSelectProps
  const searchAndSelectPropsWithClose: SearchAndSelectProps =
    selectProps.multiple === false
      ? {
          ...selectProps,
          onChange: (value: string | null) => {
            selectProps.onChange?.(value)
            handleClose()
          }
        }
      : {
          ...selectProps,
          // Only when given: an onSubmit is what shows the save bar.
          onSubmit: selectProps.onSubmit
            ? (value: string[]) => {
                selectProps.onSubmit?.(value)
                handleClose()
              }
            : undefined
        }

  return (
    <>
      <Tooltip title={tooltip}>
        <Box
          component="span"
          onClick={handleClick}
          onMouseDown={(e) => e.stopPropagation()}
          ref={ref}
          sx={{
            cursor: !disabled ? 'pointer' : undefined,
            display: 'inline-flex',
            height: 'fit-content',
            ...sx
          }}
        >
          {children}
        </Box>
      </Tooltip>

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: position ?? 'bottom' }}
        disableScrollLock
        onClose={(event) => {
          (event as any).stopPropagation()

          handleClose()
        }}
        onMouseDown={(e) => e.stopPropagation()}
        open={open}
        slotProps={{
          paper: {
            onClick: (event: any) => event?.stopPropagation(),
            onMouseDown: (event: any) => event?.stopPropagation(),
            sx: {
              width: popoverWidth
            }
          }
        }}
        transformOrigin={{
          horizontal: 'left',
          vertical: position === 'top' ? 'bottom' : 'top'
        }}
      >
        {popoverContent ? (
          <Box sx={{ p: 2, pb: 0 }}>{popoverContent}</Box>
        ) : null}
        <SearchAndSelect
          autoFocus={true}
          size="small"
          {...searchAndSelectPropsWithClose}
          sx={{ p: 2 }}
        />
      </Popover>
    </>
  )
})

SearchAndSelectPopover.displayName = 'SearchAndSelectPopover'

export default SearchAndSelectPopover
